import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/layout/Layout";
import LoginPage from "@/pages/auth/LoginPage";
import AdminDashboard from "@/pages/dashboard/AdminDashboard";
import CommandCenter from "@/pages/dashboard/CommandCenter";
import WorkActivityDashboard from "@/pages/dashboard/WorkActivityDashboard";
import CircleHeatmapPage from "@/pages/dashboard/CircleHeatmapPage";
import DTMasterPage from "@/pages/dt/DTMasterPage";
import DTDetailPage from "@/pages/dt/DTDetailPage";
import HierarchyPage from "@/pages/hierarchy/HierarchyPage";
import EmployeesPage from "@/pages/employees/EmployeesPage";
import TeamsPage from "@/pages/teams/TeamsPage";
import SurveyForm from "@/pages/field/SurveyForm";
import DTMForm from "@/pages/field/DTMForm";
import DTCForm from "@/pages/field/DTCForm";
import MIForm from "@/pages/field/MIForm";
import ShutdownsPage from "@/pages/shutdowns/ShutdownsPage";
import HoldsPage from "@/pages/holds/HoldsPage";
import QCPage from "@/pages/qc/QCPage";
import InventoryPage from "@/pages/inventory/InventoryPage";
import VehiclesPage from "@/pages/vehicles/VehiclesPage";
import FinancePage from "@/pages/finance/FinancePage";
import DailyRosterPage from "@/pages/roster/DailyRosterPage";
import AssignmentsPage from "@/pages/assignments/AssignmentsPage";
import FieldOperationsPage from "@/pages/operations/FieldOperationsPage";
import TechnicianMobilePage from "@/pages/technician/TechnicianMobilePage";
import DriverMobilePage from "@/pages/driver/DriverMobilePage";
import HelperMobilePage from "@/pages/helper/HelperMobilePage";
import MyPerformancePage from "@/pages/performance/MyPerformancePage";
import MyInventoryPage from "@/pages/inventory/MyInventoryPage";
import FieldMapPage from "@/pages/map/FieldMapPage";
import CommunicationsPage from "@/pages/communications/CommunicationsPage";
import AuditPage from "@/pages/audit/AuditPage";
import CAPResolutionPage from "@/pages/audit/CAPResolutionPage";
import FeederAssignmentsPage from "@/pages/survey/FeederAssignmentsPage";
import AttendancePage from "@/pages/attendance/AttendancePage";
import LeavePage from "@/pages/attendance/LeavePage";
import TravelPage from "@/pages/attendance/TravelPage";
import LocationMasterPage from "@/pages/locations/LocationMasterPage";
import "@/App.css";

// Mobile-only roles → their home page
const MOBILE_HOMES = { technician: "/tech-home", driver: "/driver-home", helper: "/helper-home" };

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50"><div className="text-slate-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Desktop-only route: blocks mobile roles and redirects to their mobile home
const DesktopRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50"><div className="text-slate-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const mobileHome = MOBILE_HOMES[user.role];
  if (mobileHome) return <Navigate to={mobileHome} replace />;
  return children;
};

// Shared route: Layout for desktop roles, standalone for mobile roles
const SharedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50"><div className="text-slate-500">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (MOBILE_HOMES[user.role]) return children;
  return <Layout>{children}</Layout>;
};

// Role-based home page redirect
function RoleBasedHome() {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  const mobileHome = MOBILE_HOMES[user.role];
  if (mobileHome) return <Navigate to={mobileHome} replace />;
  
  switch (user.role) {
    case "surveyor":
      return <Navigate to="/feeder-assignments" replace />;
    case "vehicle_incharge":
      return <Navigate to="/vehicles" replace />;
    default:
      return <Navigate to="/dashboard" replace />;
  }
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><RoleBasedHome /></ProtectedRoute>} />

      {/* Mobile-only pages (no Layout) */}
      <Route path="/tech-home" element={<ProtectedRoute><TechnicianMobilePage /></ProtectedRoute>} />
      <Route path="/driver-home" element={<ProtectedRoute><DriverMobilePage /></ProtectedRoute>} />
      <Route path="/helper-home" element={<ProtectedRoute><HelperMobilePage /></ProtectedRoute>} />

      {/* Field forms — standalone, no Layout (accessible by mobile roles) */}
      <Route path="/survey/new" element={<ProtectedRoute><SurveyForm /></ProtectedRoute>} />
      <Route path="/survey/:dtId" element={<ProtectedRoute><SurveyForm /></ProtectedRoute>} />
      <Route path="/dt-master/:dtId/survey" element={<ProtectedRoute><SurveyForm /></ProtectedRoute>} />
      <Route path="/dtm/:dtId" element={<SharedRoute><DTMForm /></SharedRoute>} />
      <Route path="/dtc/:dtId" element={<SharedRoute><DTCForm /></SharedRoute>} />
      <Route path="/mi/:dtId" element={<SharedRoute><MIForm /></SharedRoute>} />
      <Route path="/leave" element={<SharedRoute><LeavePage /></SharedRoute>} />

      {/* Desktop-only pages (blocked for technician/driver/helper) */}
      <Route path="/dashboard" element={<DesktopRoute><Layout><AdminDashboard /></Layout></DesktopRoute>} />
      <Route path="/command-center" element={<DesktopRoute><Layout><CommandCenter /></Layout></DesktopRoute>} />
      <Route path="/dt-master" element={<DesktopRoute><Layout><DTMasterPage /></Layout></DesktopRoute>} />
      <Route path="/dt-master/:dtId" element={<DesktopRoute><Layout><DTDetailPage /></Layout></DesktopRoute>} />
      <Route path="/hierarchy" element={<DesktopRoute><Layout><HierarchyPage /></Layout></DesktopRoute>} />
      <Route path="/employees" element={<DesktopRoute><Layout><EmployeesPage /></Layout></DesktopRoute>} />
      <Route path="/teams" element={<DesktopRoute><Layout><TeamsPage /></Layout></DesktopRoute>} />
      <Route path="/daily-roster" element={<DesktopRoute><Layout><DailyRosterPage /></Layout></DesktopRoute>} />
      <Route path="/assignments" element={<DesktopRoute><Layout><AssignmentsPage /></Layout></DesktopRoute>} />
      <Route path="/field-ops" element={<DesktopRoute><Layout><FieldOperationsPage /></Layout></DesktopRoute>} />
      <Route path="/field-map" element={<DesktopRoute><Layout><FieldMapPage /></Layout></DesktopRoute>} />
      <Route path="/my-performance" element={<DesktopRoute><Layout><MyPerformancePage /></Layout></DesktopRoute>} />
      <Route path="/my-inventory" element={<DesktopRoute><Layout><MyInventoryPage /></Layout></DesktopRoute>} />
      <Route path="/shutdowns" element={<DesktopRoute><Layout><ShutdownsPage /></Layout></DesktopRoute>} />
      <Route path="/holds" element={<DesktopRoute><Layout><HoldsPage /></Layout></DesktopRoute>} />
      <Route path="/qc" element={<DesktopRoute><Layout><QCPage /></Layout></DesktopRoute>} />
      <Route path="/inventory" element={<DesktopRoute><Layout><InventoryPage /></Layout></DesktopRoute>} />
      <Route path="/vehicles" element={<DesktopRoute><Layout><VehiclesPage /></Layout></DesktopRoute>} />
      <Route path="/finance" element={<DesktopRoute><Layout><FinancePage /></Layout></DesktopRoute>} />
      <Route path="/communications" element={<DesktopRoute><Layout><CommunicationsPage /></Layout></DesktopRoute>} />
      <Route path="/audit" element={<DesktopRoute><Layout><AuditPage /></Layout></DesktopRoute>} />
      <Route path="/audit/my-caps" element={<DesktopRoute><Layout><CAPResolutionPage /></Layout></DesktopRoute>} />
      <Route path="/work-activity" element={<DesktopRoute><Layout><WorkActivityDashboard /></Layout></DesktopRoute>} />
      <Route path="/circle-heatmap" element={<DesktopRoute><Layout><CircleHeatmapPage /></Layout></DesktopRoute>} />
      <Route path="/feeder-assignments" element={<DesktopRoute><Layout><FeederAssignmentsPage /></Layout></DesktopRoute>} />
      <Route path="/attendance" element={<DesktopRoute><Layout><AttendancePage /></Layout></DesktopRoute>} />
      <Route path="/travel" element={<DesktopRoute><Layout><TravelPage /></Layout></DesktopRoute>} />
      <Route path="/locations" element={<DesktopRoute><Layout><LocationMasterPage /></Layout></DesktopRoute>} />

      {/* Catch-all: redirects to home (mobile roles will get their mobile home via RoleBasedHome) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
